import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch, getFileUrl } from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import Header from '../../components/layout/Header';
import { Button, SearchableSelect } from '../../components/common/Common';
import useLocationFields from '../../components/common/useLocationFields';
import MediaUploader from '../../components/common/MediaUploader';
import { Save, ArrowLeft, ChevronDown, Trash2, Star, FileText, Film } from 'lucide-react';
import './AddProject.css';

const detectMediaKind = (m) => {
  const t = (m.media_type || m.type || '').toLowerCase();
  const url = m.url || m.file_url || '';
  const name = (m.name || m.file_name || '').toLowerCase();
  if (t === 'image' || t === 'floor_plan' || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url) || /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name)) return 'image';
  if (t === 'video' || /\.(mp4|webm|mov|avi|mkv)$/i.test(url) || /\.(mp4|webm|mov|avi|mkv)$/i.test(name)) return 'video';
  if (t === 'brochure' || /\.pdf$/i.test(url) || /\.pdf$/i.test(name)) return 'pdf';
  return 'file';
};

const AMENITIES_LIST = [
  'Swimming Pool', 'Gym', 'Clubhouse', 'Garden', 'Parking', 'Security',
  'Lift', 'Power Backup', 'CCTV', 'Play Area', 'Intercom', 'Fire Safety',
  'Gas Pipeline', 'Rain Water Harvesting', 'Jogging Track', 'Indoor Games',
  'Multipurpose Hall', 'Library', 'Amphitheatre', 'Sports Court',
];

const PROJECT_TYPES = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'mixed', label: 'Mixed Use' },
];

const CONSTRUCTION_STATUSES = [
  { value: 'pre-launch', label: 'Pre-Launch' },
  { value: 'under-construction', label: 'Under Construction' },
  { value: 'ready-to-move', label: 'Ready to Move' },
  { value: 'completed', label: 'Completed' },
];

const normalizeProjectType = (raw) => {
  if (!raw) return '';
  const s = String(raw).toLowerCase().trim();
  if (PROJECT_TYPES.some(t => t.value === s)) return s;
  if (s === 'mixed use') return 'mixed';
  return '';
};
const normalizeConstructionStatus = (raw) => {
  if (!raw) return '';
  const s = String(raw).toLowerCase().trim().replace(/\s+/g, '-');
  if (CONSTRUCTION_STATUSES.some(t => t.value === s)) return s;
  if (s === 'nearing-completion') return 'under-construction';
  return '';
};

// Bounds mirrored from the backend so the user is told before the request goes out.
const NUMERIC_BOUNDS = {
  latitude: { min: -90, max: 90, label: 'Latitude' },
  longitude: { min: -180, max: 180, label: 'Longitude' },
};

const CollapsibleSection = ({ title, icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="addproject__section">
      <div className="addproject__section-header" onClick={() => setOpen(o => !o)}>
        <h3 className="addproject__section-title">
          {icon}
          {title}
        </h3>
        <ChevronDown
          size={18}
          className={`addproject__section-chevron ${open ? 'addproject__section-chevron--open' : ''}`}
        />
      </div>
      {open && <div className="addproject__section-body">{children}</div>}
    </div>
  );
};

const AddProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [existingMedia, setExistingMedia] = useState([]);
  const [newFiles, setNewFiles] = useState([]);

  const [form, setForm] = useState({
    name: '',
    developer: '',
    rera_number: '',
    project_type: '',
    construction_status: '',
    possession_date: '',
    address: '',
    country: '',
    city: '',
    state: '',
    pincode: '',
    latitude: '',
    longitude: '',
    description: '',
    amenities: '',
  });

  useEffect(() => {
    if (isEditing) {
      (async () => {
        try {
          const res = await apiFetch(`projects/${id}`);
          if (res.success && res.data) {
            const p = res.data;
            setForm({
              name: p.name || '',
              developer: p.developer || '',
              rera_number: p.rera_number || '',
              project_type: normalizeProjectType(p.project_type),
              construction_status: normalizeConstructionStatus(p.construction_status),
              possession_date: p.possession_date ? p.possession_date.slice(0, 10) : '',
              address: p.address || '',
              country: p.country || '',
              city: p.city || p.location || '',
              state: p.state || '',
              pincode: p.pincode || '',
              latitude: p.latitude || '',
              longitude: p.longitude || '',
              description: p.description || '',
              amenities: p.amenities || '',
            });
            if (Array.isArray(p.media) && p.media.length > 0) {
              setExistingMedia(p.media);
            }
          }
        } catch (err) {
          showToast('Failed to load project', 'error');
        }
      })();
    }
  }, [id, isEditing, showToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const { countryProps, stateProps, cityProps } = useLocationFields({
    country: form.country,
    state: form.state,
    city: form.city,
    onChange: handleChange,
  });

  const toggleAmenity = (a) => {
    const current = form.amenities ? form.amenities.split(',').map(s => s.trim()).filter(Boolean) : [];
    const next = current.includes(a) ? current.filter(x => x !== a) : [...current, a];
    setForm(prev => ({ ...prev, amenities: next.join(', ') }));
  };

  const selectedAmenities = form.amenities ? form.amenities.split(',').map(s => s.trim()).filter(Boolean) : [];

  const deleteExistingMedia = async (mediaId) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return;
    try {
      const res = await apiFetch(`projects/${id}/media/${mediaId}`, { method: 'DELETE' });
      if (res.success) {
        setExistingMedia(prev => prev.filter(m => (m.media_id || m.id) !== mediaId));
        showToast('File deleted');
      } else {
        showToast(res.message || 'Failed to delete', 'error');
      }
    } catch (err) {
      showToast('Failed to delete', 'error');
    }
  };

  const setExistingCover = async (mediaId) => {
    try {
      const res = await apiFetch(`projects/${id}/media/${mediaId}/cover`, { method: 'PATCH' });
      if (res.success) {
        setExistingMedia(prev => prev.map(m => ({
          ...m,
          is_cover: (m.media_id || m.id) === mediaId ? 1 : 0,
        })));
        showToast('Cover image set');
      } else {
        showToast(res.message || 'Failed', 'error');
      }
    } catch (err) {
      showToast('Failed', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Project name is required';
    if (!form.developer.trim()) newErrors.developer = 'Developer is required';

    Object.entries(NUMERIC_BOUNDS).forEach(([field, rule]) => {
      const raw = form[field];
      if (raw === '' || raw === null || raw === undefined) return;
      const n = parseFloat(raw);
      if (!Number.isFinite(n)) {
        newErrors[field] = `${rule.label} must be a number`;
      } else if (n < rule.min || n > rule.max) {
        newErrors[field] = `${rule.label} must be between ${rule.min} and ${rule.max}`;
      }
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      showToast(Object.values(newErrors)[0], 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.latitude) payload.latitude = parseFloat(payload.latitude);
      if (payload.longitude) payload.longitude = parseFloat(payload.longitude);

      const endpoint = isEditing ? `projects/${id}` : 'projects';
      const method = isEditing ? 'PUT' : 'POST';
      const res = await apiFetch(endpoint, { method, body: JSON.stringify(payload) });

      if (res.success) {
        const projectId = isEditing ? id : (res.data?.project_id || res.data?.id);

        if (newFiles.length > 0 && projectId) {
          const formData = new FormData();
          newFiles.forEach(file => formData.append('media', file));
          let mediaError = null;
          try {
            const mediaRes = await apiFetch(`projects/${projectId}/media`, {
              method: 'POST',
              body: formData,
            });
            if (!mediaRes?.success) mediaError = mediaRes?.message || 'Media upload failed';
          } catch (err) {
            mediaError = err?.message || 'Media upload failed';
          }
          // The project itself saved — surface the media failure instead of
          // reporting success and silently dropping the files.
          if (mediaError) {
            showToast(`Project saved, but files did not upload: ${mediaError}`, 'error');
            setSaving(false);
            return;
          }
        }

        showToast(isEditing ? 'Project updated!' : 'Project created!');
        setTimeout(() => navigate('/projects'), 800);
      } else {
        // Backend sends { message, errors: { field: message } } for bad input —
        // pin it to the field so the user can see what to correct.
        if (res.errors && typeof res.errors === 'object') setErrors(res.errors);
        showToast(res.message || 'Failed to save', 'error');
      }
    } catch (err) {
      showToast('Failed to save project. Please check your connection and try again.', 'error');
    }
    setSaving(false);
  };

  return (
    <div className="addproject">
      <Header
        title={isEditing ? 'Edit Project' : 'Add Project'}
        subtitle={isEditing ? 'Update project details' : 'Create a new project'}
        actions={
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/projects')}>
            Back
          </Button>
        }
      />

      <div className="page">
        <form className="addproject__form" onSubmit={handleSubmit}>
          {/* Basic Info */}
          <CollapsibleSection title="Basic Information">
            <div className="addproject__section-grid">
              <div className="ab-field" style={{ gridColumn: '1/-1' }}>
                <label className="ab-field__label">Project Name <span className="ab-field__req">*</span></label>
                <input
                  className={`ab-field__input ${errors.name ? 'ab-field__input--error' : ''}`}
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. Green Valley Residences"
                />
                {errors.name && <span className="ab-field__error">{errors.name}</span>}
              </div>
              <div className="ab-field">
                <label className="ab-field__label">Developer <span className="ab-field__req">*</span></label>
                <input
                  className={`ab-field__input ${errors.developer ? 'ab-field__input--error' : ''}`}
                  name="developer"
                  value={form.developer}
                  onChange={handleChange}
                  placeholder="e.g. ABC Developers"
                />
                {errors.developer && <span className="ab-field__error">{errors.developer}</span>}
              </div>
              <div className="ab-field">
                <label className="ab-field__label">RERA Number</label>
                <input
                  className="ab-field__input"
                  name="rera_number"
                  value={form.rera_number}
                  onChange={handleChange}
                  placeholder="e.g. P52100012345"
                />
              </div>
              <div className="ab-field">
                <label className="ab-field__label">Project Type</label>
                <select className="ab-field__input ab-field__select" name="project_type" value={form.project_type} onChange={handleChange}>
                  <option value="">Select Type</option>
                  {PROJECT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="ab-field">
                <label className="ab-field__label">Construction Status</label>
                <select className="ab-field__input ab-field__select" name="construction_status" value={form.construction_status} onChange={handleChange}>
                  <option value="">Select Status</option>
                  {CONSTRUCTION_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="ab-field">
                <label className="ab-field__label">Possession Date</label>
                <input
                  className="ab-field__input"
                  type="date"
                  name="possession_date"
                  value={form.possession_date}
                  onChange={handleChange}
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* Location */}
          <CollapsibleSection title="Location">
            <div className="addproject__section-grid">
              <div className="ab-field" style={{ gridColumn: '1/-1' }}>
                <label className="ab-field__label">Address</label>
                <textarea
                  className="ab-field__input ab-field__textarea"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="Full project address"
                  rows={2}
                />
              </div>
              <SearchableSelect
                label="Country"
                compact
                className="ab-field"
                labelClassName="ab-field__label"
                errorClassName="ab-field__error"
                {...countryProps}
              />
              <SearchableSelect
                label="State"
                compact
                className="ab-field"
                labelClassName="ab-field__label"
                errorClassName="ab-field__error"
                {...stateProps}
              />
              <SearchableSelect
                label="City"
                compact
                className="ab-field"
                labelClassName="ab-field__label"
                errorClassName="ab-field__error"
                {...cityProps}
              />
              <div className="ab-field">
                <label className="ab-field__label">Pincode</label>
                <input
                  className={`ab-field__input ${errors.pincode ? 'ab-field__input--error' : ''}`}
                  name="pincode"
                  value={form.pincode}
                  onChange={handleChange}
                  placeholder="e.g. 400001"
                />
                {errors.pincode && <span className="ab-field__error">{errors.pincode}</span>}
              </div>
              <div className="ab-field">
                <label className="ab-field__label">Latitude</label>
                <input
                  className={`ab-field__input ${errors.latitude ? 'ab-field__input--error' : ''}`}
                  type="number"
                  step="any"
                  min={-90}
                  max={90}
                  name="latitude"
                  value={form.latitude}
                  onChange={handleChange}
                  placeholder="e.g. 19.076"
                />
                {errors.latitude && <span className="ab-field__error">{errors.latitude}</span>}
              </div>
              <div className="ab-field">
                <label className="ab-field__label">Longitude</label>
                <input
                  className={`ab-field__input ${errors.longitude ? 'ab-field__input--error' : ''}`}
                  type="number"
                  step="any"
                  min={-180}
                  max={180}
                  name="longitude"
                  value={form.longitude}
                  onChange={handleChange}
                  placeholder="e.g. 72.877"
                />
                {errors.longitude && <span className="ab-field__error">{errors.longitude}</span>}
              </div>
            </div>
          </CollapsibleSection>

          {/* Description */}
          <CollapsibleSection title="Description">
            <div className="addproject__section-grid">
              <div className="ab-field" style={{ gridColumn: '1/-1' }}>
                <label className="ab-field__label">Description</label>
                <textarea
                  className="ab-field__input ab-field__textarea"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Project description, highlights, key selling points..."
                  rows={4}
                />
              </div>
              <p className="ab-field__hint" style={{ gridColumn: '1/-1', margin: 0, fontSize: 12, color: 'var(--gray-500)' }}>
                Total Towers and Total Units are calculated automatically from tower data. Add towers in the Towers tab — the project total is the sum of floors × units per floor.
              </p>
            </div>
          </CollapsibleSection>

          {/* Amenities */}
          <CollapsibleSection title="Amenities">
            <div className="addproject__amenities">
              {AMENITIES_LIST.map(a => (
                <button
                  key={a}
                  type="button"
                  className={`addproject__amenity-chip ${selectedAmenities.includes(a) ? 'addproject__amenity-chip--selected' : ''}`}
                  onClick={() => toggleAmenity(a)}
                >
                  {a}
                </button>
              ))}
            </div>
          </CollapsibleSection>

          {/* Media Upload */}
          <CollapsibleSection title="Media Upload">
            {isEditing && existingMedia.length > 0 && (
              <>
                <p className="addproject__media-heading">
                  Uploaded files ({existingMedia.length})
                </p>
                <div className="addproject__media-grid">
                  {existingMedia.map(m => {
                    const mediaId = m.media_id || m.id;
                    const url = getFileUrl(m.url || m.file_url || '');
                    const kind = detectMediaKind(m);
                    const isImage = kind === 'image';
                    return (
                      <div className="addproject__media-card" key={mediaId}>
                        <div className="addproject__media-thumb">
                          {isImage ? (
                            <img src={url} alt={m.name || m.file_name || ''} />
                          ) : kind === 'video' ? (
                            <div className="addproject__media-icon"><Film size={28} /></div>
                          ) : (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="addproject__media-icon"
                              onClick={e => e.stopPropagation()}
                            >
                              <FileText size={28} />
                            </a>
                          )}
                          {m.is_cover ? <span className="addproject__media-cover-badge">Cover</span> : null}
                        </div>
                        <div className="addproject__media-meta">
                          <span className="addproject__media-name" title={m.name || m.file_name}>{m.name || m.file_name || 'File'}</span>
                          <div className="addproject__media-actions">
                            {isImage && !m.is_cover && (
                              <button
                                type="button"
                                className="addproject__media-action"
                                title="Set as cover"
                                onClick={() => setExistingCover(mediaId)}
                              >
                                <Star size={13} />
                              </button>
                            )}
                            <button
                              type="button"
                              className="addproject__media-action addproject__media-action--danger"
                              title="Delete"
                              onClick={() => deleteExistingMedia(mediaId)}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <MediaUploader
              files={newFiles}
              onFilesChange={setNewFiles}
              accept="image/*,application/pdf,video/*"
              maxFiles={20}
              label={isEditing ? 'Add more files' : 'Upload project images, brochures, or videos'}
            />
          </CollapsibleSection>

          {/* Actions */}
          <div className="addproject__actions">
            <Button variant="outline" type="button" onClick={() => navigate('/projects')}>
              Cancel
            </Button>
            <Button variant="gold" icon={Save} type="submit" disabled={saving}>
              {saving ? 'Saving...' : (isEditing ? 'Update Project' : 'Create Project')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProject;
